<script setup>
import { useVideoCallState } from "@/stores/videoCallState";
import { EventManager, Events } from "@events";
import { SectionList } from "@module-system";
import ListContainer from "@module-system/ViewModule/ListContainer.vue";
import StackedCallButtonsListContainer from "@module-system/ViewModule/StackedCallButtonsListContainer.vue";
import SectionToggleContainer from "@module-system/ViewModule/SectionToggleContainer.vue";
import axios from "axios";

import { ref, onMounted, onUnmounted } from "vue";
import VideoContainer from "./VideoContainer.vue";
import i18n from "@/i18n";

const props = defineProps({
  rtcId: {
    type: String,
    required: true,
  },
  role: {
    type: String,
    required: true,
  },
  name: {
    type: String,
    required: true,
  },
});

axios.defaults.headers.common["ECV_RtcId"] = props.rtcId;

const nameProp = ref(props.name);
const roleProp = ref(props.role);
const holdStyle = window.CONFIG.HOLD_STYLE || "dialog";

const searchParams = new URLSearchParams(window.location.search);
const isTest = searchParams.get("test");

const rootEventEmitter = EventManager.rootEventEmitter();
const state = useVideoCallState(props.rtcId);

const loadingText = ref("");
const loading = ref(true);

const licenseStatus = ref(false);
const licenseError = ref("");

const isAgent = props.role == "agent";
const maxStackableBtns = ref(8);

state.setName(nameProp.value);
state.setRole(roleProp.value);
let agentToken = null;
if (isAgent) {
  agentToken = searchParams.get("agentToken");
  axios.defaults.headers.common["ECV_AgentToken"] = agentToken;
  state.setAgentToken(agentToken);
}

console.info(`VideoCall: set name: ${nameProp.value}, role: ${roleProp.value}`);

async function getAgentCallDetails() {
  loadingText.value = i18n.global.t("GettingCallDetails");
  // if url has test=true, then it is a test call
  if (isTest) {
    // save test video call
    // generate random agent token ( 32 characters guid )

    await axios
      .post("/api/testcall", {
        rtcId: props.rtcId,
        callerAttributes: {
          name: props.name,
          role: props.role,
          test: true,
          ECV_AgentToken: agentToken,
        },
        agent: {
          username: props.name,
        },
      })
      .then((response) => {
        console.info("VideoCall: post call");
        console.info(response.data);
        state.setCallData(response.data.data);
      })
      .catch((error) => {
        console.error(error.toString());
        console.error(error.response.data);
      });
  }
  await axios
    .get("/api/call/byrtcId/" + props.rtcId)
    .then((response) => {
      console.info("VideoCall: get call by rtcId");
      console.info(response.data);
      state.setCallData(response.data.data);
    })
    .catch((error) => {
      console.error(error.toString());
      console.error(error.response.data);
    });
}
async function checkLicense() {
  console.info("License=> checkLicense");
  loadingText.value = i18n.global.t("CheckingLicense");
  const licRes = await axios.post("/api/license/claim/" + nameProp.value + "?rtcId=" + props.rtcId);

  console.info("license checked");
  console.info(licRes.data);

  loadingText.value = "";
  licenseStatus.value = licRes.data.status;
  licenseError.value = licRes.data.reason;

  // if status is true, claim license again in 1 minute
  if (licRes.data.status) {
    console.info("will claim license again in 1 minute");
    setTimeout(() => {
      checkLicense();
    }, 60000);
  }
  return licRes.data.status;
}

async function agentStartup() {
  checkLicense().then((status) => {
    loading.value = false;
  });
  getAgentCallDetails();
  sendVideoCallStartedEvent();
}

if (props.role == "agent") {
  agentStartup();
} else {
  sendVideoCallStartedEvent();
  loading.value = false;
}

function sendVideoCallStartedEvent() {
  rootEventEmitter.emit(Events.VideoCallCreated, { rtcId: props.rtcId });
}

onMounted(() => {
  console.info("VideoCallComponent mounted");

  // Getting hangUpRedirectUrl from queryStringParam, if set, override default redirect page after hangup (for users only)
  const hangUpRedirectUrl = searchParams.get("hangUpRedirectUrl");
  if (hangUpRedirectUrl) {
    window.CONFIG.CLOSING_URL.USER = hangUpRedirectUrl;
  }

  window.addEventListener("resize", windowResized);
  windowResized();
});

onUnmounted(() => {
  window.removeEventListener("resize", windowResized);
});

function windowResized() {
  maxStackableBtns.value = Math.floor((window.innerHeight * 0.75) / 60);
}

const middleContent = ref(null);
</script>

<template>
  <ListContainer :rtc-id="props.rtcId" :section-type="SectionList.AbsoluteTop"> </ListContainer>
  <div class="video-call" v-if="!isAgent || (isAgent && (loading || licenseStatus))">
    <SectionToggleContainer
      class="left-panel"
      :rtc-id="props.rtcId"
      :section-type="SectionList.CallLeftPanel"
      :size="150"></SectionToggleContainer>
    <div class="middle-content" ref="middleContent" id="middleContent">
      <div attach="#middleContent" v-if="state.isHeld && holdStyle == 'top-sheet'" width="auto">
        <v-alert :title="$t('INFO')" :text="$t('WEBRTC_CALL_HOLD_MESSAGE')" type="info" icon="mdi-timer-sand"></v-alert>
      </div>
      <v-dialog attach="#middleContent" v-if="state.isHeld && holdStyle == 'dialog'" :model-value="true" persistent width="auto">
        <v-alert :title="$t('INFO')" :text="$t('WEBRTC_CALL_HOLD_MESSAGE')" type="info" icon="mdi-timer-sand"></v-alert>
      </v-dialog>
      <SectionToggleContainer
        class="top-panel"
        :rtc-id="props.rtcId"
        :section-type="SectionList.CallTopPanel"
        :size="100"></SectionToggleContainer>
      <div class="video-call-content position-relative">
        <ListContainer :rtc-id="props.rtcId" :section-type="SectionList.CallTopBar"> </ListContainer>
        <div class="call-buttons-list">
          <StackedCallButtonsListContainer
            v-if="!state.isDisconnected && !state.isHeld"
            :rtc-id="props.rtcId"
            :section-type="SectionList.CallButtonsList"
            :max-stackable-btns="maxStackableBtns"></StackedCallButtonsListContainer>
        </div>
        <VideoContainer :rtc-id="props.rtcId"></VideoContainer>
      </div>
      <SectionToggleContainer
        class="bottom-panel"
        :rtc-id="props.rtcId"
        :section-type="SectionList.CallBottomPanel"
        :close-disabled="true"
        :size="100"></SectionToggleContainer>
    </div>
    <SectionToggleContainer
      class="right-panel"
      :rtc-id="props.rtcId"
      :section-type="SectionList.CallRightPanel"
      :manuel-open="true"
      :size="150"></SectionToggleContainer>
    <div style="flex-direction: column">
      <ListContainer :rtc-id="props.rtcId" :section-type="SectionList.CallModal"></ListContainer>
    </div>

    <v-snackbar v-model="state.snackbar.show" timeout="-1" color="info" bottom center class="snackbar">
      <div class="snackbar-container">
        {{ state.snackbar.msg }}
      </div>
    </v-snackbar>
  </div>
  <div v-else class="d-flex justify-content-center align-items-center h-100" style="flex-direction: column">
    <v-progress-circular size="220" width="20" color="red" :model-value="100">
      <v-icon icon="mdi-close" size="150"></v-icon>
    </v-progress-circular>
    <span class="text-white mt-3 fw-bold fs-4">{{ $t(licenseError) }}</span>
    <span class="text-white mt-3 fs-6">{{ $t("LICENSE_ERROR_MESSAGE") }}</span>
  </div>
  <ListContainer v-if="!loading" :rtc-id="props.rtcId" :section-type="SectionList.AbsoulteBottom"> </ListContainer>
</template>

<style lang="scss">
.video-call {
  display: flex;
  height: 100%;
  width: 100%;
}

.left-panel {
  height: 100%;
  border-right: 3px dashed #363636;
}

.middle-content {
  flex: 1;
  flex-grow: 1;
  flex-shrink: 1;
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

#middleContent.middle-content {
  position: relative;
  .v-overlay.v-overlay--active,
  .v-overlay__scrim {
    position: absolute !important;
  }
}

.video-call-content {
  flex: 1;
  width: 100%;
  flex-grow: 1;
  flex-shrink: 1;
  position: relative;
  overflow: hidden;
}

.right-panel {
  height: 100%;
  border-left: 3px dashed #363636;
  .section-close-button.right {
    z-index: 2500;
  }
}

.call-buttons-list {
  position: absolute;
  top: 0;
  left: 25px;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9;
  flex-direction: column;
  height: 100%;
}

.call-buttons-list .ecv-call-button {
  margin-top: 10px;
}

.top-panel {
  border-bottom: 3px dashed #363636;
}

.bottom-panel {
  height: 10%;
  border-top: 3px dashed #363636;
}

.snackbar {
  margin-bottom: 50px;

  -webkit-user-select: none; /* Safari */
  -ms-user-select: none; /* IE 10 and IE 11 */
  user-select: none; /* Standard syntax */
}

.snackbar-container {
  width: 100%;
  text-align: center;
  margin: 0 auto;
}
</style>
